import type { RankedStory } from './feedsV2';

export interface DraftRow {
  key: string;
  rank: number;
  title: string;
  url: string | null;
  canonical_domain: string | null;
  /** In the draft's list and not the live feed's (`added`), the reverse (`dropped`), or both (`kept`). */
  change: 'kept' | 'added' | 'dropped';
}

export interface DraftComparison {
  live: DraftRow[];
  draft: DraftRow[];
  added: number;
  dropped: number;
}

/** A story's identity across two builds: its topic, else its face article (as For You's combine has it). */
export function draftStoryKey(story: RankedStory): string {
  return story.topic_id || story.id;
}

/**
 * The live feed's and a draft description's top *k* stories side by side: a
 * draft row is `added` when the live top *k* lacks its story, a live row
 * `dropped` when the draft's top *k* lacks it.
 */
export function compareDraft(live: RankedStory[], draft: RankedStory[], k = 20): DraftComparison {
  const liveTop = live.slice(0, k);
  const draftTop = draft.slice(0, k);
  const liveKeys = new Set(liveTop.map(draftStoryKey));
  const draftKeys = new Set(draftTop.map(draftStoryKey));
  const row = (story: RankedStory, index: number, change: DraftRow['change']): DraftRow => ({
    key: draftStoryKey(story), rank: index + 1, title: story.title || story.id, url: story.url ?? null,
    canonical_domain: story.canonical_domain ?? null, change,
  });
  const liveRows = liveTop.map((story, index) => row(story, index, draftKeys.has(draftStoryKey(story)) ? 'kept' : 'dropped'));
  const draftRows = draftTop.map((story, index) => row(story, index, liveKeys.has(draftStoryKey(story)) ? 'kept' : 'added'));
  return {
    live: liveRows, draft: draftRows,
    added: draftRows.filter(r => r.change === 'added').length,
    dropped: liveRows.filter(r => r.change === 'dropped').length,
  };
}
